import type { FieldSensitivity } from '../../../core/fields/types/FieldManifest';
import type { FieldManifest } from '../../../core/fields/types/FieldManifest';
import {
  FIELD_SENSITIVITY_ORDER,
  fieldSensitivityRank,
} from '../../../core/fields/security';
import {
  ORG_CHART_FIELD_PACKS,
  FIELD_SECTIONS,
  getFieldSection,
} from '../../orgchart/fields';

export type FieldVisibilityConfig = {
  sensitivityLevel: FieldSensitivity;
  includeSections?: string[];
  excludeSections?: string[];
};

export const SENSITIVITY_ORDER: FieldSensitivity[] = [...FIELD_SENSITIVITY_ORDER];

export const SENSITIVITY_LABELS: Record<FieldSensitivity, string> = {
  public: 'Public',
  internal: 'Internal',
  manager: 'Manager',
  hr_confidential: 'HR Confidential',
  hr_restricted: 'HR Restricted',
  sensitive_pii: 'Sensitive PII',
};

const STRUCTURAL_KEYS = new Set([
  'id', 'type', 'name', 'color', 'fill', 'photoRef', 'objectKey',
  'customFields', 'visibleFields', 'dataBinding',
  'x', 'y', 'w', 'h', 'width', 'height',
  'text', 'parentId', 'collapsed',
  // Baked by buildPeopleChartScene to mark a node kept only for reporting-chain
  // connectivity (not a filter match) — structural projection metadata, not a
  // data field, so it must survive the public viewer's field-visibility redaction
  // or the /ov viewer can never dim these nodes.
  'projectionContext',
]);

function sensitivityIndex(level: FieldSensitivity): number {
  return fieldSensitivityRank(level);
}

function fieldAttrKey(field: FieldManifest): string {
  return field.namespace ? `${field.namespace}.${field.id}` : field.id;
}

function fieldSectionId(field: FieldManifest): string | undefined {
  if (!field.tags || field.tags.length === 0) return undefined;
  const section = getFieldSection(field.tags as string[]);
  return section?.id;
}

export function buildAllowedAttrKeys(config: FieldVisibilityConfig): Set<string> {
  const maxIndex = sensitivityIndex(config.sensitivityLevel);
  const includeSet = config.includeSections ? new Set(config.includeSections) : null;
  const excludeSet = config.excludeSections ? new Set(config.excludeSections) : null;

  const allowed = new Set<string>();

  for (const pack of ORG_CHART_FIELD_PACKS) {
    for (const field of pack.fields) {
      const key = fieldAttrKey(field);
      const fieldLevel = field.sensitivity ?? 'internal';
      const sectionId = fieldSectionId(field);

      // Check section overrides first
      if (excludeSet && sectionId && excludeSet.has(sectionId)) {
        continue;
      }
      if (includeSet && sectionId && includeSet.has(sectionId)) {
        allowed.add(key);
        if (field.namespace) allowed.add(field.id);
        continue;
      }

      // Check sensitivity threshold
      if (sensitivityIndex(fieldLevel) <= maxIndex) {
        allowed.add(key);
        if (field.namespace) allowed.add(field.id);
      }
    }
  }

  return allowed;
}

export function buildRedactedAttrKeys(config: FieldVisibilityConfig): Set<string> {
  const maxIndex = sensitivityIndex(config.sensitivityLevel);
  const includeSet = config.includeSections ? new Set(config.includeSections) : null;
  const excludeSet = config.excludeSections ? new Set(config.excludeSections) : null;

  const redacted = new Set<string>();

  for (const pack of ORG_CHART_FIELD_PACKS) {
    for (const field of pack.fields) {
      const key = fieldAttrKey(field);
      const fieldLevel = field.sensitivity ?? 'internal';
      const sectionId = fieldSectionId(field);

      if (includeSet && sectionId && includeSet.has(sectionId)) {
        continue;
      }

      if (
        (excludeSet && sectionId && excludeSet.has(sectionId)) ||
        sensitivityIndex(fieldLevel) > maxIndex
      ) {
        redacted.add(key);
        if (field.namespace) redacted.add(field.id);
      }
    }
  }

  return redacted;
}

export function filterNodeAttrs(
  attrs: Record<string, unknown>,
  allowedKeys: Set<string>,
): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(attrs)) {
    if (STRUCTURAL_KEYS.has(key) || allowedKeys.has(key)) {
      result[key] = value;
    }
  }
  return result;
}

export function getSectionVisibilityDefaults(level: FieldSensitivity): {
  included: string[];
  excluded: string[];
} {
  const maxIndex = sensitivityIndex(level);
  const included: string[] = [];
  const excluded: string[] = [];

  for (const section of FIELD_SECTIONS) {
    const sectionFields = getAllFieldsForSection(section.id);
    if (sectionFields.length === 0) continue;

    // A section is included if at least one of its fields passes the threshold
    const hasAllowed = sectionFields.some(
      (f) => sensitivityIndex(f.sensitivity ?? 'internal') <= maxIndex,
    );
    if (hasAllowed) {
      included.push(section.id);
    } else {
      excluded.push(section.id);
    }
  }

  return { included, excluded };
}

function getAllFieldsForSection(sectionId: string): FieldManifest[] {
  const result: FieldManifest[] = [];
  for (const pack of ORG_CHART_FIELD_PACKS) {
    for (const field of pack.fields) {
      if (fieldSectionId(field) === sectionId) {
        result.push(field);
      }
    }
  }
  return result;
}
